import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import QRCodeStyling from "qr-code-styling";
import {
  fetchCreditPacks,
  createCreditsCheckout,
  lookupVat,
} from "../api/billing";

const STEPS = {
  PICKING_PACK: "picking-pack",
  BILLING: "billing",
  CHECKOUT: "checkout",
  QR: "qr",
  SUCCESS: "success",
};

const getCheckoutPackProps = (pack) => ({
  ...(pack?.packId ? { packId: pack.packId } : {}),
  ...(pack?.creditsAmount !== undefined
    ? { creditsAmount: pack.creditsAmount }
    : {}),
  ...(pack?.priceCents !== undefined ? { priceCents: pack.priceCents } : {}),
});

const BILLING_FIELDS = [
  {
    name: "billingCompanyName",
    label: "Ragione sociale / nominativo per fattura",
    required: true,
  },
  { name: "vat", label: "Partita IVA / Codice Fiscale", required: true },
  { name: "billingAddress", label: "Indirizzo", required: true },
  { name: "billingCity", label: "Città", required: true },
  { name: "billingZipCode", label: "CAP", required: true },
  {
    name: "pec",
    label: "PEC (opzionale, per fattura elettronica)",
    required: false,
  },
  {
    name: "electronicInvoicingId",
    label: "Codice destinatario SDI (opzionale)",
    required: false,
  },
];

const formatEur = (cents) =>
  ((Number(cents) || 0) / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

const OFC_SITE_BASE = "https://onoranzefunebricloud.com";
const OFC_HANDLE = "@onoranzefunebricloud";

const buildOfcUrl = (source) =>
  `${OFC_SITE_BASE}/?utm_source=${source}&utm_medium=share&utm_campaign=cards-share`;

const buildPublicCopy = (url) =>
  `Per chi lavora nel settore funebre: sto provando Eterno di OnoranzeFunebriCloud, la piattaforma modulare con oltre 40 moduli che digitalizza ogni processo dell'agenzia (gestione, vendite, contabilità, marketing). Davvero ben fatto: ${url}`;

const buildStoryCopy = (url) =>
  `Eterno di ${OFC_HANDLE} — la piattaforma modulare con oltre 40 moduli per digitalizzare ogni processo del settore funebre. ${url}`;

const buildWarmIntroCopy = (url) =>
  `Ciao! Volevo segnalarti Eterno di OnoranzeFunebriCloud, una piattaforma gestionale modulare pensata per il settore funebre — agenzie, case funerarie, crematori, centri servizi. Ha oltre 40 moduli per digitalizzare ogni processo, da gestione e produttività a vendite e contabilità. Lo trovo davvero ben fatto, secondo me ti può interessare: ${url}`;

const buildShareLinks = () => {
  const liUrl = buildOfcUrl("linkedin");
  const fbUrl = buildOfcUrl("facebook");
  const emailUrl = buildOfcUrl("email");
  const waUrl = buildOfcUrl("whatsapp");
  return {
    linkedin: {
      url: liUrl,
      copy: buildPublicCopy(liUrl),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(liUrl)}`,
    },
    facebook: {
      url: fbUrl,
      copy: buildPublicCopy(fbUrl),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fbUrl)}`,
    },
    instagram: {
      url: buildOfcUrl("instagram"),
      copy: buildStoryCopy(buildOfcUrl("instagram")),
      href: "https://www.instagram.com/onoranzefunebricloud/",
    },
    email: {
      url: emailUrl,
      copy: buildPublicCopy(emailUrl),
      href: `mailto:?subject=${encodeURIComponent(
        "Eterno — il gestionale modulare per il settore funebre",
      )}&body=${encodeURIComponent(buildPublicCopy(emailUrl))}`,
    },
    whatsapp: {
      url: waUrl,
      copy: buildWarmIntroCopy(waUrl),
      href: `https://wa.me/?text=${encodeURIComponent(buildWarmIntroCopy(waUrl))}`,
    },
  };
};

const SHARE_LINKS = buildShareLinks();

let stripePromise;
const getStripe = () => {
  if (stripePromise) return stripePromise;
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    return Promise.reject(
      new Error(
        "Stripe publishable key non configurata (REACT_APP_STRIPE_PUBLISHABLE_KEY).",
      ),
    );
  }
  stripePromise = loadStripe(key);
  return stripePromise;
};

const buildInitialBilling = (card) => {
  const initial = {};
  BILLING_FIELDS.forEach(({ name }) => {
    initial[name] = card?.[name] || "";
  });
  return initial;
};

const BuyCreditsModal = ({
  open,
  onClose,
  onSuccess,
  card,
  token,
  onTrack,
}) => {
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [step, setStep] = useState(STEPS.PICKING_PACK);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [billing, setBilling] = useState({});
  const [saveToCard, setSaveToCard] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [vatLookupBusy, setVatLookupBusy] = useState(false);
  const [vatLookupError, setVatLookupError] = useState("");
  const [qrLoadingPackId, setQrLoadingPackId] = useState(null);
  const [qrSession, setQrSession] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");
  const [selectedShareChannelId, setSelectedShareChannelId] = useState(null);

  const handleVatLookup = async () => {
    const rawVat = (billing.vat || "").trim();
    if (!rawVat) return;
    setVatLookupBusy(true);
    setVatLookupError("");
    onTrack?.("vat_lookup_submitted", { source: "credits_checkout" });
    try {
      const data = await lookupVat({ token, vat: rawVat });
      const c = data.company || {};
      setBilling((current) => ({
        ...current,
        billingCompanyName: c.ragioneSociale || current.billingCompanyName,
        billingAddress: c.via || current.billingAddress,
        billingCity: c.citta || current.billingCity,
        billingZipCode: c.cap || current.billingZipCode,
        electronicInvoicingId:
          c.codiceUnivocoSdi || current.electronicInvoicingId,
        pec: c.pec || current.pec,
      }));
      onTrack?.("vat_lookup_succeeded", {
        source: "credits_checkout",
        resultSource: data.source || "",
      });
    } catch (err) {
      setVatLookupError(
        err.payload?.message === "INVALID_VAT"
          ? "Partita IVA non valida (servono 11 cifre)."
          : err.payload?.message === "NOT_FOUND"
            ? "Nessuna azienda trovata per questa Partita IVA."
            : err.message,
      );
      onTrack?.("vat_lookup_failed", {
        source: "credits_checkout",
        reason: err.payload?.message || "request_failed",
        statusCode: err.statusCode,
      });
    } finally {
      setVatLookupBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    onTrack?.("credits_modal_opened", {
      source: "avatar_ai",
    });
    setStep(STEPS.PICKING_PACK);
    setSelectedPackId(null);
    setError("");
    setClientSecret(null);
    setMissingFields([]);
    setBilling(buildInitialBilling(card));
    setSaveToCard(false);
    setQrSession(null);
    setQrLoadingPackId(null);
    setCopiedKey("");
    setSelectedShareChannelId(null);
  }, [open, card, onTrack]);

  const copyToClipboard = useCallback(async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      if (key.startsWith("channel-")) {
        onTrack?.("credits_free_copy_clicked", {
          channel: key.replace("channel-", ""),
        });
      }
      setTimeout(() => {
        setCopiedKey((current) => (current === key ? "" : current));
      }, 1800);
    } catch {
      setCopiedKey("");
    }
  }, [onTrack]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const loadPacks = useCallback(async () => {
    setLoadingPacks(true);
    setError("");
    try {
      const data = await fetchCreditPacks({ token });
      setPacks(data.data || []);
      onTrack?.("credits_packs_loaded", {
        packsCount: (data.data || []).length,
      });
    } catch (err) {
      setError(err.message);
      onTrack?.("credits_packs_load_failed", {
        statusCode: err.statusCode,
      });
    } finally {
      setLoadingPacks(false);
    }
  }, [token, onTrack]);

  useEffect(() => {
    if (open) loadPacks();
  }, [open, loadPacks]);

  const selectedPack = useMemo(
    () => packs.find((p) => p.packId === selectedPackId),
    [packs, selectedPackId],
  );

  const startCheckout = useCallback(
    async ({ override }) => {
      setSubmitting(true);
      setError("");
      setMissingFields([]);

      try {
        const res = await createCreditsCheckout({
          token,
          packId: selectedPackId,
          billingOverride: override,
          saveToCard,
        });
        setClientSecret(res.clientSecret);
        setStep(STEPS.CHECKOUT);
        onTrack?.("credits_checkout_started", {
          ...getCheckoutPackProps(selectedPack),
          saveToCard: Boolean(saveToCard),
        });
      } catch (err) {
        if (err.statusCode === 422 && err.payload?.missingFields) {
          onTrack?.("credits_checkout_requires_billing", {
            ...getCheckoutPackProps(selectedPack),
            missingFields: err.payload.missingFields,
          });
          setMissingFields(err.payload.missingFields);
          if (err.payload.billingFromCard) {
            setBilling((current) => ({
              ...err.payload.billingFromCard,
              ...current,
            }));
          }
          setStep(STEPS.BILLING);
        } else {
          setError(err.message);
          onTrack?.("credits_checkout_failed", {
            ...getCheckoutPackProps(selectedPack),
            statusCode: err.statusCode,
            step,
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [token, selectedPackId, saveToCard, selectedPack, step, onTrack],
  );

  const handlePackContinue = () => {
    if (!selectedPackId) return;
    startCheckout({ override: undefined });
  };

  const handleShowQr = async (packId) => {
    if (!packId) return;
    setQrLoadingPackId(packId);
    setError("");
    try {
      const res = await createCreditsCheckout({
        token,
        packId,
        mode: "hosted",
      });
      const pack = packs.find((p) => p.packId === packId);
      setQrSession({ url: res.url, pack });
      setSelectedPackId(packId);
      setStep(STEPS.QR);
      onTrack?.("credits_qr_checkout_started", {
        ...getCheckoutPackProps(pack),
      });
    } catch (err) {
      setError(err.message);
      onTrack?.("credits_qr_checkout_failed", {
        packId,
        statusCode: err.statusCode,
      });
    } finally {
      setQrLoadingPackId(null);
    }
  };

  const handleBillingSubmit = (event) => {
    event.preventDefault();
    const trimmed = {};
    BILLING_FIELDS.forEach(({ name }) => {
      const value = (billing[name] || "").trim();
      if (value) trimmed[name] = value;
    });
    onTrack?.("credits_billing_submitted", {
      ...getCheckoutPackProps(selectedPack),
      missingFieldsCount: missingFields.length,
      saveToCard: Boolean(saveToCard),
    });
    startCheckout({ override: trimmed });
  };

  const closeModal = () => {
    onTrack?.("credits_modal_closed", {
      step,
      ...(selectedPackId ? { selectedPackId } : {}),
    });
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-2 py-3 sm:px-3 sm:py-6">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl max-h-[95vh] sm:max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b">
          <Icon
            icon="mdi:gift-outline"
            className="h-7 w-7 text-emerald-600"
          />
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Ottieni altri crediti
            </h2>
            <p className="text-sm text-slate-500">
              Acquista un pacchetto oppure sblocca crediti gratis.
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="ml-auto rounded-full p-2 text-slate-400 hover:bg-slate-100"
            aria-label="Chiudi"
          >
            <Icon icon="mdi:close" className="h-5 w-5" />
          </button>
        </div>


        <div className="px-4 py-4 sm:px-6 sm:py-5">
          {error && (
            <div className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {step === STEPS.PICKING_PACK && (
            <>
              <FreeCreditsPanel
                copiedKey={copiedKey}
                onCopy={copyToClipboard}
                selectedChannelId={selectedShareChannelId}
                onSelectChannel={setSelectedShareChannelId}
                onTrack={onTrack}
              />

              <div className="mt-8 mb-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-500">
                  Oppure acquista un pacchetto
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {loadingPacks ? (
                <div className="py-12 text-center text-slate-500">
                  Caricamento pacchetti…
                </div>
              ) : packs.length === 0 ? (
                <div className="py-12 text-center text-slate-500">
                  Nessun pacchetto disponibile al momento.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {packs.map((pack) => {
                    const isSelected = selectedPackId === pack.packId;
                    const isQrBusy = qrLoadingPackId === pack.packId;
                    return (
                      <div
                        key={pack.packId}
                        onClick={() => {
                          setSelectedPackId(pack.packId);
                          onTrack?.("credits_pack_selected", {
                            ...getCheckoutPackProps(pack),
                          });
                        }}
                        className={`text-left rounded-lg border-2 p-4 transition cursor-pointer ${
                          isSelected
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="text-lg font-bold text-slate-900">
                            {pack.name}
                          </span>
                          <span className="text-xl font-extrabold text-emerald-700">
                            {formatEur(pack.priceCents)}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {pack.creditsAmount} crediti AI · IVA esclusa
                        </p>
                        {pack.description && (
                          <p className="mt-2 text-xs text-slate-500">
                            {pack.description}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowQr(pack.packId);
                          }}
                          disabled={isQrBusy || Boolean(qrLoadingPackId)}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                        >
                          <Icon icon="mdi:qrcode-scan" className="text-xl" />
                          {isQrBusy ? "Genero QR…" : "Paga con telefono"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {selectedShareChannelId && (
                  <button
                    type="button"
                    onClick={() => setSelectedShareChannelId(null)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                  >
                    <Icon icon="mdi:chevron-left" className="h-4 w-4" />
                    Indietro
                  </button>
                )}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    disabled={!selectedPackId || submitting}
                    onClick={handlePackContinue}
                    className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {submitting ? "Verifico…" : "Continua"}
                  </button>
                </div>
              </div>
            </>
          )}

          {step === STEPS.BILLING && (
            <form onSubmit={handleBillingSubmit}>
              <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                Per emettere fattura B2B servono i tuoi dati di fatturazione.
                {missingFields.length > 0 && (
                  <span className="block mt-1 text-xs">
                    Campi mancanti: {missingFields.join(", ")}
                  </span>
                )}
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {BILLING_FIELDS.map((field) => {
                  const isVat = field.name === "vat";
                  return (
                    <label key={field.name} className="block">
                      <span className="text-xs font-bold text-slate-700">
                        {field.label}
                        {field.required && (
                          <span className="text-rose-500">&nbsp;*</span>
                        )}
                      </span>
                      {isVat ? (
                        <div className="mt-1 flex gap-2">
                          <input
                            type="text"
                            value={billing[field.name] || ""}
                            onChange={(e) =>
                              setBilling((current) => ({
                                ...current,
                                [field.name]: e.target.value,
                              }))
                            }
                            required={field.required}
                            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={handleVatLookup}
                            disabled={vatLookupBusy || !(billing.vat || "").trim()}
                            className="inline-flex items-center justify-center gap-1 rounded-md bg-slate-800 px-3 py-2 text-xs font-bold text-white hover:bg-slate-700 disabled:opacity-50"
                          >
                            <Icon icon="mdi:magnify" className="text-base" />
                            {vatLookupBusy ? "..." : "Cerca"}
                          </button>
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={billing[field.name] || ""}
                          onChange={(e) =>
                            setBilling((current) => ({
                              ...current,
                              [field.name]: e.target.value,
                            }))
                          }
                          required={field.required}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                        />
                      )}
                      {isVat && vatLookupError && (
                        <span className="mt-1 block text-[11px] text-rose-600">
                          {vatLookupError}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <label className="mt-4 inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={saveToCard}
                  onChange={(e) => setSaveToCard(e.target.checked)}
                  className="h-4 w-4"
                />
                Salva questi dati sulla mia Cards per i prossimi acquisti
              </label>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setStep(STEPS.PICKING_PACK)}
                  className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600"
                >
                  Indietro
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {submitting ? "Avvio checkout…" : "Procedi al pagamento"}
                </button>
              </div>
            </form>
          )}

          {step === STEPS.CHECKOUT && clientSecret && (
            <div className="min-h-[520px]">
              <EmbeddedCheckoutProvider
                stripe={getStripe()}
                options={{
                  clientSecret,
                  onComplete: () => {
                    setStep(STEPS.SUCCESS);
                    onTrack?.("credits_checkout_completed", {
                      ...getCheckoutPackProps(selectedPack),
                    });
                    if (onSuccess) onSuccess();
                  },
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
              {selectedPack && (
                <p className="mt-3 text-center text-xs text-slate-500">
                  {selectedPack.name} · {formatEur(selectedPack.priceCents)} (IVA escl.)
                </p>
              )}
            </div>
          )}

          {step === STEPS.QR && qrSession && (
            <QrPanel
              url={qrSession.url}
              pack={qrSession.pack}
              onBack={() => {
                setQrSession(null);
                setStep(STEPS.PICKING_PACK);
              }}
            />
          )}

          {step === STEPS.SUCCESS && (
            <div className="py-12 text-center">
              <Icon
                icon="mdi:check-circle"
                className="mx-auto h-16 w-16 text-emerald-500"
              />
              <h3 className="mt-4 text-xl font-bold text-slate-900">
                Pagamento riuscito
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                I crediti saranno accreditati entro pochi secondi. Riceverai email
                di conferma.
              </p>
              <button
                type="button"
                onClick={closeModal}
                className="mt-6 rounded-md bg-emerald-600 px-5 py-2 text-sm font-bold text-white"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SHARE_CHANNELS = [
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: "mdi:linkedin",
    iconBg: "bg-[#0A66C2]",
    cardBg: "bg-gradient-to-br from-[#0A66C2]/10 via-white to-[#0A66C2]/5",
    creditsAmount: 3,
    description:
      "Pubblica un post pubblico su LinkedIn raccontando Eterno. Il tuo network del settore funebre lo vedrà nel feed.",
    cta: "Apri LinkedIn e pubblica",
    href: SHARE_LINKS.linkedin.href,
    copy: SHARE_LINKS.linkedin.copy,
    note: "LinkedIn non precompila automaticamente il post: copia il testo qui sotto e incollalo nella casella che si apre.",
  },
  {
    id: "facebook",
    name: "Facebook",
    icon: "mdi:facebook",
    iconBg: "bg-[#1877F2]",
    cardBg: "bg-gradient-to-br from-[#1877F2]/10 via-white to-[#1877F2]/5",
    creditsAmount: 3,
    description:
      "Pubblica un post pubblico sulla tua bacheca Facebook raccontando Eterno.",
    cta: "Apri Facebook e pubblica",
    href: SHARE_LINKS.facebook.href,
    copy: SHARE_LINKS.facebook.copy,
    note: "Facebook precompila l'URL ma non il testo: copia e incolla quello qui sotto.",
  },
  {
    id: "instagram",
    name: "Instagram",
    icon: "mdi:instagram",
    iconBg: "bg-gradient-to-br from-pink-500 to-purple-500",
    cardBg: "bg-gradient-to-br from-pink-100 via-white to-purple-50",
    creditsAmount: 5,
    description: `Pubblica una storia su Instagram parlando di Eterno e taggando ${OFC_HANDLE}. Senza tag visibile niente crediti: il team verifica dal vivo.`,
    cta: "Apri Instagram",
    href: SHARE_LINKS.instagram.href,
    copy: SHARE_LINKS.instagram.copy,
    note: "Instagram non permette il share automatico delle storie. Apri l'app, crea la storia, ricordati il tag.",
  },
  {
    id: "email",
    name: "Email",
    icon: "mdi:email-outline",
    iconBg: "bg-slate-700",
    cardBg: "bg-gradient-to-br from-slate-100 via-white to-slate-50",
    creditsAmount: 3,
    description:
      "Manda un'email a un collega o alla tua mailing list parlando di Eterno.",
    cta: "Apri client email",
    href: SHARE_LINKS.email.href,
    copy: SHARE_LINKS.email.copy,
    note: null,
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: "mdi:whatsapp",
    iconBg: "bg-[#25D366]",
    cardBg: "bg-gradient-to-br from-emerald-100 via-white to-emerald-50",
    creditsAmount: 10,
    badge: "TOP",
    description:
      "Manda un WhatsApp a un collega del settore funebre presentandogli Eterno. +10 crediti per ogni collega presentato — il modo più veloce per accumulare crediti.",
    cta: "Apri WhatsApp con messaggio pronto",
    href: SHARE_LINKS.whatsapp.href,
    copy: SHARE_LINKS.whatsapp.copy,
    note: null,
  },
];

const FreeCreditsPanel = ({
  copiedKey,
  onCopy,
  selectedChannelId,
  onSelectChannel,
  onTrack,
}) => {
  const selected = SHARE_CHANNELS.find((c) => c.id === selectedChannelId);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-50 via-amber-50 to-emerald-50 p-4 text-center">
        <p className="text-xs font-extrabold uppercase tracking-wider text-fuchsia-700">
          Crediti gratis
        </p>
        <h3 className="mt-1 text-lg font-extrabold text-slate-900">
          Aiutaci a far conoscere Eterno
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Scegli un canale, condividi e mostra la prova al nostro team:
          accreditiamo i crediti sulla tua Cards.
        </p>
      </div>

      {!selected && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
            {SHARE_CHANNELS.map((channel) => (
              <ChannelTile
                key={channel.id}
                channel={channel}
                onSelect={() => {
                  onTrack?.("credits_free_channel_selected", {
                    channel: channel.id,
                    creditsAmount: channel.creditsAmount,
                  });
                  onSelectChannel(channel.id);
                }}
              />
            ))}
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
            <Icon
              icon="mdi:hand-wave"
              className="mt-0.5 h-4 w-4 text-amber-500 shrink-0"
            />
            <span>
              Quando hai condiviso, mostra il post / la chat dal vivo al nostro
              team e accreditiamo i crediti sulla tua Cards entro pochi minuti.
            </span>
          </div>
        </>
      )}

      {selected && (
        <ChannelDetail
          channel={selected}
          copiedKey={copiedKey}
          onCopy={onCopy}
          onBack={() => onSelectChannel(null)}
          onTrack={onTrack}
        />
      )}
    </div>
  );
};

const ChannelTile = ({ channel, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-slate-200 p-2 transition hover:scale-[1.04] hover:shadow-lg hover:border-slate-300 ${channel.cardBg}`}
    >
      <span className="absolute -right-1.5 -top-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold text-amber-900 shadow">
        +{channel.creditsAmount}
      </span>
      {channel.badge && (
        <span className="absolute -left-1.5 -top-1.5 rounded-full bg-fuchsia-600 px-1.5 py-0.5 text-[9px] font-extrabold text-white shadow">
          {channel.badge}
        </span>
      )}
      <div
        className={`flex h-11 w-11 items-center justify-center rounded-xl text-white shadow ${channel.iconBg}`}
      >
        <Icon icon={channel.icon} className="h-6 w-6" />
      </div>
      <span className="text-[11px] font-bold text-slate-700">
        {channel.name}
      </span>
    </button>
  );
};

const ChannelDetail = ({ channel, copiedKey, onCopy, onBack, onTrack }) => {
  const copyKey = `channel-${channel.id}`;
  const isCopied = copiedKey === copyKey;
  return (
    <div className={`rounded-2xl border-2 border-slate-200 p-5 ${channel.cardBg}`}>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-700"
      >
        <Icon icon="mdi:chevron-left" className="h-4 w-4" />
        Tutti i canali
      </button>

      <div className="mt-3 flex items-start gap-3">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow ${channel.iconBg}`}
        >
          <Icon icon={channel.icon} className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-extrabold text-slate-900">
              {channel.name}
            </h3>
            <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-extrabold text-amber-900 shadow">
              +{channel.creditsAmount} crediti
            </span>
            {channel.badge && (
              <span className="rounded-full bg-fuchsia-600 px-2 py-0.5 text-[10px] font-extrabold text-white shadow">
                {channel.badge}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-700">{channel.description}</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-white/80 border border-slate-200 p-3 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Anteprima messaggio
          </span>
          <button
            type="button"
            onClick={() => onCopy(copyKey, channel.copy)}
            className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold transition ${
              isCopied
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Icon
              icon={isCopied ? "mdi:check" : "mdi:content-copy"}
              className="h-3 w-3"
            />
            {isCopied ? "Copiato" : "Copia testo"}
          </button>
        </div>
        <p className="mt-2 text-sm text-slate-800 leading-snug whitespace-pre-line break-words [overflow-wrap:anywhere]">
          {channel.copy}
        </p>
      </div>

      {channel.note && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800 flex items-start gap-2">
          <Icon
            icon="mdi:lightbulb-outline"
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span>{channel.note}</span>
        </div>
      )}

      <a
        href={channel.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          onTrack?.("credits_free_share_opened", {
            channel: channel.id,
            creditsAmount: channel.creditsAmount,
          })
        }
        className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold text-white shadow-md transition hover:opacity-95 ${channel.iconBg}`}
      >
        <Icon icon={channel.icon} className="h-5 w-5" />
        {channel.cta}
      </a>

      <p className="mt-3 text-center text-[11px] text-slate-500">
        Quando hai pubblicato, mostra la prova al nostro team per ricevere i
        crediti.
      </p>
    </div>
  );
};

const QrPanel = ({ url, pack, onBack }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !url) return undefined;
    node.replaceChildren();
    const qr = new QRCodeStyling({
      width: 520,
      height: 520,
      data: url,
      dotsOptions: { color: "#047857", type: "extra-rounded" },
      cornersSquareOptions: { type: "extra-rounded", color: "#065f46" },
      backgroundOptions: { color: "#ffffff" },
    });
    qr.append(node);
    return () => {
      node.replaceChildren();
    };
  }, [url]);

  return (
    <div className="py-4">
      <div className="flex flex-col items-center text-center">
        <h3 className="text-lg font-bold text-slate-900">
          Inquadra con il telefono
        </h3>
        <p className="mt-1 max-w-sm text-sm text-slate-600">
          Apri la fotocamera del telefono e scansiona il codice. Il pagamento
          si completerà su Stripe.
        </p>
        <div
          ref={containerRef}
          className="mt-4 max-w-full overflow-hidden rounded-xl border border-slate-200 p-3 [&>canvas]:max-w-full [&>canvas]:h-auto [&>svg]:max-w-full [&>svg]:h-auto"
        />
        {pack && (
          <p className="mt-3 text-sm text-slate-700">
            <span className="font-semibold">{pack.name}</span> ·{" "}
            {formatEur(pack.priceCents)} (IVA escl.)
          </p>
        )}
        <div className="mt-4 flex flex-col items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-emerald-700 hover:underline"
          >
            Apri il link su questo dispositivo
          </a>
          <button
            type="button"
            onClick={onBack}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600"
          >
            Torna ai pacchetti
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;
